import type { CSSProperties, PointerEvent, ReactNode } from "react";
import { T } from "../tokens";

export interface TextBtnProps {
  children: ReactNode;
  onClick?: () => void;
  color?: string;
  size?: number;
  ls?: string;
  style?: CSSProperties;
  mono?: boolean;
  disabled?: boolean;
  /**
   * Day 13 PR-C (D9) reverted: static state is pure text. `framed={true}`
   * (the new behavior) adds a press-state background (ink-veil-08) + scale
   * 0.97 + opacity 0.7 so non-engineering users get clear tap feedback,
   * but the resting state stays clean — no extra lines on the page.
   *
   * `framed={false}` keeps the very old behavior (opacity only) for inline
   * status-text-style CTAs where you don't want the tap region to flash a
   * background.
   */
  framed?: boolean;
}

export function TextBtn({
  children,
  onClick,
  color = T.ink900,
  size = 16,
  ls = "0.2em",
  style,
  mono = true,
  disabled = false,
  framed = true,
}: TextBtnProps) {
  // Day 14: press feedback amplified — old scale(0.97) was too subtle for
  // non-engineering users to register as "I tapped it". scale(0.93) +
  // letter-spacing compression + slightly darker ink-veil background reads
  // as "the button got pressed inward".
  const handlePress = (e: PointerEvent<HTMLButtonElement>, pressed: boolean) => {
    const el = e.currentTarget;
    if (disabled) return;
    if (pressed && framed) {
      el.style.background = "rgba(14,14,14,0.12)";
      el.style.transform = "scale(0.93)";
      el.style.opacity = "0.78";
      el.style.letterSpacing = "0.14em";
    } else {
      el.style.background = "transparent";
      el.style.transform = "scale(1)";
      el.style.opacity = framed ? "1" : pressed ? "0.5" : "1";
      el.style.letterSpacing = ls;
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onPointerDown={(e) => handlePress(e, true)}
      onPointerUp={(e) => handlePress(e, false)}
      onPointerLeave={(e) => handlePress(e, false)}
      style={{
        fontFamily: mono ? T.mono : T.han,
        fontSize: size,
        letterSpacing: ls,
        color: disabled ? T.ink350 : color,
        textTransform: mono ? "uppercase" : "none",
        minHeight: 44,
        minWidth: 44,
        padding: "10px 14px",
        background: "transparent",
        border: "none",
        transition:
          "background 180ms var(--ease-mist), transform 180ms var(--ease-mist), opacity 180ms var(--ease-mist), letter-spacing 180ms var(--ease-mist)",
        cursor: disabled ? "default" : "pointer",
        touchAction: "manipulation",
        ...style,
      }}
    >
      {children}
    </button>
  );
}
